import { container } from '@databases/service-container';
import { proxy } from 'valtio';

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

const VIEW_SETTINGS_MENU_TYPES: ReadonlyArray<MenuData['type']> = [
  'view-actions',
  'view-filter',
  'view-sort',
  'view-group',
  'view-layout',
  'view-hide-fields'
];

export type MenuData =
  | {
      type: 'field';
      fieldId: string;
      panel?: boolean;
    }
  | {
      type: 'entry';
      entryId: string;
      panel?: boolean;
    }
  | {
      type: 'value';
      fieldId: string;
      entryId: string;
      panel?: boolean;
    }
  | {
      type: 'view-actions';
      viewId: string;
      source?: 'view-tabs' | 'view-picker';
    }
  | {
      type: 'view-filter';
      viewId: string;
      fieldIdForFilterToCreate?: string;
    }
  | {
      type: 'view-sort';
      viewId: string;
    }
  | {
      type: 'view-group';
      viewId: string;
    }
  | {
      type: 'view-layout';
      viewId: string;
    }
  | {
      type: 'view-hide-fields';
      viewId: string;
    };

interface ContextualMenuState {
  menuData: MenuData | null;
  showMenu: (menu: MenuData) => void;
  closeMenu: () => void;
  isMenuOpen: (menu: MenuData | null) => boolean;
  isAnyMenuOpen: () => boolean;
  isAnyViewSettingsMenuOpen: () => boolean;
  isPanel: () => boolean;
}

export const contextualMenuState = proxy<ContextualMenuState>({
  menuData: null,
  showMenu(menu: MenuData) {
    this.menuData = menu;

    try {
      const eventBus = container.resolve('EventBus');
      eventBus.dispatch('databaseContextMenuShown', null);
    } catch (e) {
      console.error(e);
    }
  },
  closeMenu() {
    this.menuData = null;
  },
  isMenuOpen(menu: MenuData | null): boolean {
    if (!menu) {
      return this.menuData !== null;
    }

    const type = menu?.type;
    if (!type) {
      return false;
    }
    switch (type) {
      case 'field':
        return this.menuData?.type === 'field' && this.menuData.fieldId === menu?.fieldId;
      case 'entry':
        return this.menuData?.type === 'entry' && this.menuData.entryId === menu?.entryId;
      case 'value':
        return (
          this.menuData?.type === 'value' &&
          this.menuData.fieldId === menu?.fieldId &&
          this.menuData.entryId === menu?.entryId
        );
      case 'view-actions':
        return (
          this.menuData?.type === 'view-actions' &&
          this.menuData.viewId === menu?.viewId &&
          (this.menuData.source ?? 'view-tabs') === (menu.source ?? 'view-tabs')
        );
      case 'view-filter':
      case 'view-sort':
      case 'view-group':
      case 'view-layout':
      case 'view-hide-fields':
        return this.menuData?.type === type && this.menuData.viewId === menu?.viewId;
      default:
        return unreachable(type);
    }
  },
  isAnyMenuOpen(): boolean {
    return this.menuData !== null;
  },
  isAnyViewSettingsMenuOpen(): boolean {
    if (!this.menuData) {
      return false;
    }
    return VIEW_SETTINGS_MENU_TYPES.includes(this.menuData.type);
  },
  isPanel(): boolean {
    if (this.menuData && 'panel' in this.menuData) {
      return !!this.menuData.panel;
    }
    return false;
  }
});
